import type { JsonApiResource } from 'tio-data/demo/types';
import type { DemoSessionSelection } from 'tio-employee/utils/career-pathways-demo-data';
import {
  getCurrentDemoClientId,
  getCurrentDemoProductIds,
  getCurrentDemoRoleId,
  getCurrentDemoUserScenarioId,
  getCurrentDemoTaPaymentModelId,
  getCurrentDemoPslfScenarioId,
  getCurrentDemoDebtLevelId,
  getCurrentDemoDocumentStatusId,
  getCurrentDemoTasScenarioId,
  persistDemoSession,
} from 'tio-employee/utils/career-pathways-demo-data';
import type { PslfFormFixture } from './products/pslf-fixtures';

/** The separate employer approver's employee id; numeric because production reviewer matching uses numeric ids. */
export const DEMO_STORY_APPROVER_EMPLOYEE_ID = 9001;

export type DemoStoryId = 'for-profit-ta-coursecash' | 'non-profit-pslf-ta';
export type DemoStoryStage =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'attend'
  | 'evidence-submitted'
  | 'evidence-approved'
  | 'paid';

/** Complete happy-path presets; amounts and application progress are shared across role changes. */
export const DEMO_STORIES = [
  {
    id: 'for-profit-ta-coursecash',
    employee: { firstName: 'Jordan', lastName: 'Lee' },
    name: 'For-profit with TA + CourseCash',
    description:
      'Explore TA programs, apply for course funding, receive approval, then complete the course and see CourseCash tuition and reimbursement payments.',
    selection: {
      clientId: 'lifepoint',
      productIds: ['tuitionAssistance'],
      roleId: 'employee',
      userScenarioId: 'new-employee',
      taPaymentModelId: 'prepayment',
      tasScenarioId: 'not-started',
      pslfScenarioId: 'blank-slate',
      debtLevelId: 'not-linked',
      documentStatusId: 'applied',
    },
  },
  {
    id: 'non-profit-pslf-ta',
    employee: { firstName: 'Jordan', lastName: 'Lee' },
    name: 'Non-profit with PSLF + TA',
    description:
      'Complete a first PSLF employment certification with an employer reviewer, then apply for TA and follow the course through reimbursement.',
    selection: {
      clientId: 'advocate',
      productIds: ['tuitionAssistance', 'pslf'],
      roleId: 'employee',
      userScenarioId: 'new-employee',
      taPaymentModelId: 'reimbursement',
      tasScenarioId: 'not-started',
      pslfScenarioId: 'blank-slate',
      debtLevelId: 'medium',
      documentStatusId: 'applied',
    },
  },
] satisfies {
  id: DemoStoryId;
  name: string;
  employee: { firstName: string; lastName: string };
  description: string;
  selection: DemoSessionSelection;
}[];

interface StoryState {
  id: DemoStoryId;
  stage: DemoStoryStage;
  resources: JsonApiResource[];
  templateId?: string;
  applicationCreated?: boolean;
  pslfForms?: PslfFormFixture[];
  evidenceSubmittedAt?: string;
  evidenceApprovedAt?: string;
  paidAt?: string;
  receiptVerifiedAt?: string;
  asOf?: string;
  submittedAt?: string;
  approvedAt?: string;
  approvalAmounts?: { reimbursement: number; tuition: number };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const validLink = (value: unknown) =>
  isRecord(value) && typeof value['id'] === 'string' && typeof value['type'] === 'string';
const validRelationships = (value: unknown) =>
  isRecord(value) &&
  Object.values(value).every(
    (relationship) =>
      isRecord(relationship) &&
      (relationship['data'] === null ||
        (Array.isArray(relationship['data'])
          ? relationship['data'].every(validLink)
          : validLink(relationship['data'])))
  );
const validFields = (value: unknown) =>
  isRecord(value) &&
  Object.values(value).every(
    (field) => isRecord(field) && (field['values'] === undefined || Array.isArray(field['values']))
  );

/** Reads the saved local story; malformed or obsolete data is ignored. */
export function readDemoStory(): StoryState | null {
  try {
    const state = JSON.parse(
      localStorage.getItem('reimagined-demo-story') ?? 'null'
    ) as StoryState | null;
    if (
      !state ||
      !DEMO_STORIES.some((story) => story.id === state.id) ||
      ![
        'draft',
        'submitted',
        'approved',
        'attend',
        'evidence-submitted',
        'evidence-approved',
        'paid',
      ].includes(state.stage) ||
      !Array.isArray(state.resources) ||
      (state.pslfForms !== undefined &&
        (!Array.isArray(state.pslfForms) ||
          state.pslfForms.some(
            (form) =>
              !isRecord(form) ||
              typeof form['id'] !== 'string' ||
              !['borrower_signed', 'awaiting_submit', 'submitted'].includes(
                String(form['status'])
              ) ||
              typeof form['employmentStartDate'] !== 'string' ||
              (form['employerFields'] !== undefined && !isRecord(form['employerFields'])) ||
              (form['borrowerFields'] !== undefined && !isRecord(form['borrowerFields']))
          ))) ||
      [
        state.asOf,
        state.submittedAt,
        state.approvedAt,
        state.evidenceSubmittedAt,
        state.evidenceApprovedAt,
        state.paidAt,
        state.receiptVerifiedAt,
      ].some(
        (timestamp) =>
          timestamp !== undefined &&
          (typeof timestamp !== 'string' || Number.isNaN(Date.parse(timestamp)))
      ) ||
      (state.templateId !== undefined && typeof state.templateId !== 'string') ||
      (state.applicationCreated !== undefined && typeof state.applicationCreated !== 'boolean') ||
      (state.approvalAmounts !== undefined &&
        (!isRecord(state.approvalAmounts) ||
          ![state.approvalAmounts.reimbursement, state.approvalAmounts.tuition].every(
            (amount) => typeof amount === 'number' && Number.isSafeInteger(amount) && amount >= 0
          ))) ||
      state.resources.some(
        (resource) =>
          !resource ||
          typeof resource.id !== 'string' ||
          typeof resource.type !== 'string' ||
          (resource.relationships !== undefined && !validRelationships(resource.relationships)) ||
          (resource.attributes !== undefined &&
            (!isRecord(resource.attributes) ||
              ('fields' in resource.attributes && !validFields(resource.attributes['fields']))))
      )
    ) {
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

/** Resolves persisted progress only for the same preset axes; employee/admin handoffs are permitted. */
export function resolveDemoStory(selection: DemoSessionSelection) {
  const story = readDemoStory();
  const preset = DEMO_STORIES.find((candidate) => candidate.id === story?.id)?.selection;
  if (!story || !preset || !['employee', 'admin'].includes(selection.roleId)) return null;
  if (
    selection.productIds.length !== preset.productIds.length ||
    !preset.productIds.every((id) => selection.productIds.includes(id))
  )
    return null;
  for (const key of ['clientId', 'taPaymentModelId', 'documentStatusId'] as const)
    if (selection[key] !== preset[key]) return null;
  if (
    preset.productIds.some((id) => id === 'pslf') &&
    selection.pslfScenarioId !== preset.pslfScenarioId
  )
    return null;
  if (
    (selection.tasScenarioId ?? getCurrentDemoTasScenarioId()) !==
    (selection.roleId === 'employee' ? preset.tasScenarioId : 'in-progress')
  )
    return null;
  if (
    selection.roleId === 'employee' &&
    (selection.userScenarioId !== preset.userScenarioId ||
      selection.debtLevelId !== preset.debtLevelId)
  )
    return null;
  if (selection.roleId === 'admin' && selection.userScenarioId !== 'power-user') return null;
  return story;
}

/** The validated local Story for URL/storage-driven fixture builders. */
export function readActiveDemoStory() {
  return resolveDemoStory({
    clientId: getCurrentDemoClientId(),
    productIds: getCurrentDemoProductIds(),
    roleId: getCurrentDemoRoleId(),
    userScenarioId: getCurrentDemoUserScenarioId(),
    taPaymentModelId: getCurrentDemoTaPaymentModelId(),
    pslfScenarioId: getCurrentDemoPslfScenarioId(),
    debtLevelId: getCurrentDemoDebtLevelId(),
    documentStatusId: getCurrentDemoDocumentStatusId(),
  });
}

/** Starts a fresh local run and applies its preset axes; discards the previous Story's progress. */
export function startDemoStory(id: DemoStoryId) {
  const preset = DEMO_STORIES.find((story) => story.id === id);
  if (!preset) return;
  persistDemoSession(preset.selection);
  saveDemoStory({ id, stage: 'draft', resources: [] });
}

/** Persists fixture resources and timestamps locally and notifies the Story indicator; never writes to the API. */
export function saveDemoStory(state: StoryState) {
  localStorage.setItem('reimagined-demo-story', JSON.stringify(state));
  window.dispatchEvent(new Event('demo-story-changed'));
}

/** Clears story progress when an operator switches to individual demo options. */
export function clearDemoStory() {
  localStorage.removeItem('reimagined-demo-story');
  window.dispatchEvent(new Event('demo-story-changed'));
}
